import { createColumnHelper } from '@tanstack/react-table'
import { createTableColumnHelper } from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { ContactResponse } from 'models/contacts'
import { useMemo } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { formatDateWithTimeAndMinutes } from 'utils/formatting'
import { SEQUENCE_COLUMNS } from './sequenceColumnInfo'

// DS2026 badge geometry — pill (16px radius), 14px Medium with -0.2px tracking,
// 50/700 tinted color pairs matching the First Bite badge color scheme.
const CHIP_BASE =
  'inline-flex items-center rounded-2xl px-2.5 py-0.5 text-sm font-medium leading-5 tracking-[-0.2px]'

const STATUS_CONFIG: Record<
  string,
  { label: string; className: string; tooltip?: string }
> = {
  in_sequence: {
    label: 'Active',
    className: 'bg-blue-50 text-blue-700',
  },
  sequence_finished: {
    label: 'Completed',
    className: 'bg-green-50 text-green-700',
  },
  paused: {
    label: 'Paused',
    className: 'bg-amber-50 text-amber-700',
  },
  unsubscribed: {
    label: 'Unsubscribed',
    className: 'bg-orange-50 text-orange-700',
  },
  bounced: {
    label: 'Bounced',
    className: 'bg-red-50 text-red-700',
    tooltip: 'Email bounced. Find a new address for this contact.',
  },
  queued: {
    label: 'Queued',
    className: 'bg-indigo-50 text-indigo-700',
  },
  never_contacted: {
    label: 'Never Contacted',
    className: 'bg-neutral-100 text-neutral-700',
  },
  replied: {
    label: 'Replied',
    className: 'bg-purple-50 text-purple-700',
  },
  removed: {
    label: 'Removed',
    className: 'bg-rose-50 text-rose-700',
  },
}

const NOT_IN_SEQUENCE_CONFIG = {
  label: 'Not in Sequence',
  className: 'bg-amber-50 text-amber-700',
}

function SequenceStatusChip({ status }: { status: string | null | undefined }) {
  const config: { label: string; className: string; tooltip?: string } = status
    ? STATUS_CONFIG[status] || {
        label: status,
        className: 'bg-gray-100 text-gray-800',
      }
    : NOT_IN_SEQUENCE_CONFIG

  const chip = (
    <span className={`${CHIP_BASE} ${config.className}`}>{config.label}</span>
  )
  if (!config.tooltip) return chip
  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip>{config.tooltip}</Tooltip>}
    >
      {chip}
    </OverlayTrigger>
  )
}

function NumericCell({ value }: { value: number | null | undefined }) {
  if (value == null)
    return <span className="text-fg-secondary">{'\u2013'}</span>
  return <span className="tabular-nums">{value}</span>
}

export function useSequenceColumns() {
  const { preferences } = usePreferences()
  const tooltips: Record<string, unknown> = preferences?.tooltips || {}

  const columnHelper = createColumnHelper<ContactResponse>()
  const seqColumnHelper = createTableColumnHelper({
    columns: SEQUENCE_COLUMNS,
    tooltips,
  })

  return useMemo(
    () => [
      columnHelper.display({
        ...(seqColumnHelper.retrieveColumn('seq_membership_status') as any),
        id: 'seq_membership_status',
        size: 160,
        cell: (info: any) => {
          const row = info.row.original
          return <SequenceStatusChip status={row.seq_membership_status} />
        },
      }),
      columnHelper.accessor('seq_scheduled_emails', {
        ...(seqColumnHelper.retrieveColumn('seq_scheduled_emails') as any),
        size: 150,
        cell: (info: any) => <NumericCell value={info.getValue()} />,
      }),
      columnHelper.accessor('seq_emails_sent', {
        ...(seqColumnHelper.retrieveColumn('seq_emails_sent') as any),
        size: 120,
        cell: (info: any) => <NumericCell value={info.getValue()} />,
      }),
      columnHelper.accessor('seq_replies', {
        ...(seqColumnHelper.retrieveColumn('seq_replies') as any),
        size: 120,
        cell: (info: any) => <NumericCell value={info.getValue()} />,
      }),
      columnHelper.accessor('seq_last_email', {
        ...(seqColumnHelper.retrieveColumn('seq_last_email') as any),
        size: 200,
        cell: (info: any) => {
          const value = info.getValue()
          if (!value)
            return <span className="text-fg-secondary">{'\u2013'}</span>
          return (
            <span className="text-sm tabular-nums">
              {formatDateWithTimeAndMinutes(new Date(value))}
            </span>
          )
        },
      }),
      columnHelper.display({
        ...(seqColumnHelper.retrieveColumn('seq_sequence_step') as any),
        id: 'seq_sequence_step',
        size: 150,
        cell: (info: any) => {
          const row = info.row.original
          if (row.seq_steps_completed == null || row.seq_total_steps == null)
            return <span className="text-fg-secondary">{'\u2013'}</span>
          return (
            <span className="text-sm">
              <span className="font-medium tabular-nums">
                {row.seq_steps_completed}
              </span>
              <span className="text-fg-secondary">
                {' '}
                of {row.seq_total_steps}
              </span>
            </span>
          )
        },
      }),
    ],
    [preferences, tooltips]
  )
}
